/**
 * StatusPicker — colored pill grid replacing the <select> in state changers.
 *
 * Recommended transitions are shown at full opacity.
 * Non-recommended are muted (opacity-40) but still selectable.
 * The selected pill gets a subtle ring.
 */

export interface StatusOption {
  key: string;
  label: string;
  color: string;
  recommended_transitions?: string[];
}

const BG: Record<string, string> = {
  blue:   'bg-blue-100 text-blue-800 hover:bg-blue-200',
  cyan:   'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
  teal:   'bg-teal-100 text-teal-800 hover:bg-teal-200',
  green:  'bg-green-100 text-green-800 hover:bg-green-200',
  yellow: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200',
  orange: 'bg-orange-100 text-orange-800 hover:bg-orange-200',
  red:    'bg-red-100 text-red-800 hover:bg-red-200',
  purple: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
  indigo: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
  gray:   'bg-gray-100 text-gray-600 hover:bg-gray-200',
};

const RING: Record<string, string> = {
  blue:   'ring-blue-500',
  cyan:   'ring-cyan-500',
  teal:   'ring-teal-500',
  green:  'ring-green-500',
  yellow: 'ring-yellow-500',
  orange: 'ring-orange-500',
  red:    'ring-red-500',
  purple: 'ring-purple-500',
  indigo: 'ring-indigo-500',
  gray:   'ring-gray-400',
};

interface StatusPickerProps {
  options: StatusOption[];
  currentKey: string;
  selected: string;
  onSelect: (key: string) => void;
}

export default function StatusPicker({ options, currentKey, selected, onSelect }: StatusPickerProps) {
  const current = options.find(o => o.key === currentKey);
  const recommended = current?.recommended_transitions ?? [];

  const choices = options.filter(o => o.key !== currentKey);

  return (
    <div className="flex flex-wrap gap-2">
      {choices.map(opt => {
        const isRecommended = recommended.includes(opt.key);
        const isSelected = selected === opt.key;
        const bg = BG[opt.color] ?? BG.gray;
        const ring = RING[opt.color] ?? RING.gray;

        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(isSelected ? '' : opt.key)}
            className={[
              'px-3 py-1 rounded-full text-xs font-medium transition-all',
              bg,
              isSelected ? `ring-2 ring-offset-1 ${ring}` : '',
              !isRecommended ? 'opacity-40' : '',
            ].join(' ')}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
